
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { 
  ChartContainer, 
  ChartTooltip, 
  ChartTooltipContent
} from '@/components/ui/chart';
import { ClicksByCategory } from '@/hooks/product/useProductAnalytics';
import { ArrowUp, ArrowDown } from 'lucide-react';

interface ClicksByCategoryChartProps {
  data: ClicksByCategory[];
}

// Define proper type for the CustomTooltip props
interface CustomTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: any;
}

export function ClicksByCategoryChart({ data }: ClicksByCategoryChartProps) {
  // Define chart colors configuration
  const chartConfig = {
    clicks: {
      label: 'Current Period',
      color: '#8884d8'
    },
    previous_clicks: {
      label: 'Previous Period',
      color: '#82ca9d'
    }
  };

  // Check if comparison data exists
  const hasComparisonData = data.some(item => item.previous_clicks !== undefined);

  // Create custom tooltip to show growth indicators
  const CustomTooltip = ({ active, payload, label }: CustomTooltipProps) => {
    if (active && payload && payload.length) {
      const categoryData = payload[0].payload;
      return (
        <div className="bg-background border p-3 rounded-md shadow-md">
          <p className="font-semibold">{categoryData.category}</p>
          <p>Current: {categoryData.clicks} clicks</p>
          {categoryData.previous_clicks !== undefined && (
            <p>Previous: {categoryData.previous_clicks} clicks</p>
          )}
          {categoryData.growth !== undefined && (
            <div className="flex items-center text-sm mt-1">
              {categoryData.growth >= 0 ? (
                <ArrowUp className="h-3 w-3 text-emerald-500 mr-1" />
              ) : (
                <ArrowDown className="h-3 w-3 text-rose-500 mr-1" />
              )}
              <span className={categoryData.growth >= 0 ? "text-emerald-500" : "text-rose-500"}>
                {Math.abs(categoryData.growth)}%
              </span>
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Clicks By Category</CardTitle>
        <CardDescription>Compare performance by product category</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          {data.length > 0 ? (
            <ChartContainer config={chartConfig}>
              <BarChart data={data} margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="category" 
                  tickLine={true}
                  axisLine={true}
                />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar 
                  dataKey="clicks" 
                  radius={[4, 4, 0, 0]}
                  fill={chartConfig.clicks.color}
                />
                {hasComparisonData && (
                  <Bar 
                    dataKey="previous_clicks" 
                    radius={[4, 4, 0, 0]}
                    fill={chartConfig.previous_clicks.color}
                    fillOpacity={0.7}
                  />
                )}
              </BarChart>
            </ChartContainer>
          ) : (
            <div className="flex justify-center items-center h-full">
              <p className="text-muted-foreground">No category data available for this period</p>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
